import propTypes from 'prop-types';
import { useMemo, useContext } from 'react';
import { useMutation } from 'react-apollo';
import { useTranslation } from 'react-i18next';
import { mutations } from '@kiper/account-graphql/invite';
import { formatDate, getUserLang, apolloErrorHandler } from '@kiper/fns';
import { useSwal } from '@kiper/hooks';
import { PersonalizedInviteContext } from './context';

const PersonalizedInviteProvider = ({
  children,
  inviteData,
  loading,
  error,
}) => {
  const [t] = useTranslation('personalized-invite');
  const { toast } = useSwal();
  const [language] = useMemo(() => getUserLang().split('-'), []);

  const isIOS = useMemo(() => {
    const userAgent = navigator?.userAgent || window?.opera;
    return /iPad|iPhone|iPod/i.test(userAgent);
  }, []);

  const handleApolloError = err => {
    const formattedErrors = apolloErrorHandler(err);
    if (formattedErrors && formattedErrors.length) {
      toast.fire({ title: formattedErrors.join('\n'), icon: 'error' });
    } else {
      toast.fire({
        title: t('google-wallet-error'),
        icon: 'error',
      });
    }
  };

  const formatPass = useMemo(() => {
    let pass;
    if (inviteData?.id && inviteData?.qrCode) {
      pass = {
        hashId: inviteData.qrCode,
        guestId: inviteData.id,
        inviteName: inviteData.inviteTitle || '',
        startText:
          inviteData.eventDate &&
          formatDate(inviteData.eventDate, 'L - HH:mm').formatted,
        start: inviteData.eventDate,
        end: inviteData.eventEndDate,
        condominiumName: inviteData.location?.name || '',
        unityGroup: '',
        unity: '',
        streetName: inviteData.location?.address || '',
        addressNumber: '',
        isPorterPartner: inviteData.isPorterPartner || false,
        i18n: {
          language,
          title: t('pass.title'),
          subheader: t('pass.subheader'),
          date: t('pass.date'),
          local: t('pass.local'),
          address: t('pass.address'),
        },
      };
    }
    return pass;
  }, [inviteData, language, t]);

  const [getPass, { loading: loadingPass }] = useMutation(
    mutations.createPass,
    {
      variables: { data: formatPass },
      onCompleted: res => {
        window.open(res?.getInvitePass?.hash, '_blank');
      },
      onError: err => handleApolloError(err),
    },
  );

  const values = useMemo(
    () => ({
      isIOS,
      inviteData,
      customization: inviteData?.customization,
      location: inviteData?.location,
      eventInfo: {
        title: inviteData?.inviteTitle,
        date: inviteData?.eventDate,
        endDate: inviteData?.eventEndDate,
        destination: inviteData?.destination,
      },
      hostMessage: inviteData?.hostMessage,
      condominiumNotice: inviteData?.condominiumNotice,
      hasFacialRegistered: inviteData?.hasFacialRegistered || false,
      userName: inviteData?.userName,
      previewFacePhotoUrl: inviteData?.previewFacePhotoUrl,
      hasSmartLock: !!inviteData?.hasSmartLock,
      loading,
      error,
      getPass,
      loadingPass,
      language,
    }),
    [inviteData, loading, error, isIOS, getPass, loadingPass, language],
  );

  return (
    <PersonalizedInviteContext.Provider value={values}>
      {children}
    </PersonalizedInviteContext.Provider>
  );
};

export const usePersonalizedInviteContext = () => {
  const context = useContext(PersonalizedInviteContext);
  if (!context) {
    throw new Error(
      'usePersonalizedInviteContext must be used within a PersonalizedInviteProvider',
    );
  }
  return context;
};

PersonalizedInviteProvider.propTypes = {
  children: propTypes.node.isRequired,
  inviteData: propTypes.shape({
    id: propTypes.number,
    inviteTitle: propTypes.string,
    eventDate: propTypes.string,
    eventEndDate: propTypes.string,
    qrCode: propTypes.string,
    isPorterPartner: propTypes.bool,
    location: propTypes.shape({
      name: propTypes.string,
      address: propTypes.string,
      mapsUrl: propTypes.string,
    }),
    destination: propTypes.string,
    customization: propTypes.shape({
      primaryColor: propTypes.string,
      secondaryColor: propTypes.string,
      logoUrl: propTypes.string,
      bannerImageUrl: propTypes.string,
      bannerPosition: propTypes.string,
    }),
    hostMessage: propTypes.string,
    condominiumNotice: propTypes.string,
    hasFacialRegistered: propTypes.bool,
    facialPhoto: propTypes.string,
    userName: propTypes.string,
    previewFacePhotoUrl: propTypes.string,
    hasSmartLock: propTypes.bool,
  }),
  loading: propTypes.bool,
  error: propTypes.shape({}),
};

PersonalizedInviteProvider.defaultProps = {
  inviteData: null,
  loading: false,
  error: null,
};

export default PersonalizedInviteProvider;
